import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface AuthContextType {
  token: string | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  token: null,
  isAuthenticated: false,
  login: async () => {},
  logout: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

const TOKEN_KEY = 'finfactor_auth_token';

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

function isTokenValid(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload) return false;

  const iss = typeof payload.iss === 'string' ? payload.iss : '';
  const aud = typeof payload.aud === 'string' ? payload.aud : '';
  const exp = typeof payload.exp === 'number' ? payload.exp : 0;

  if (!iss.includes('finfactor')) return false;
  if (!aud.includes('finfactor')) return false;
  if (exp * 1000 <= Date.now()) return false;

  return true;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => {
    try {
      const stored = localStorage.getItem(TOKEN_KEY);
      if (stored && isTokenValid(stored)) return stored;
      if (stored) localStorage.removeItem(TOKEN_KEY);
    } catch {
      // ignore
    }
    return null;
  });

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  // Periodic expiry check every 60s
  useEffect(() => {
    if (!token) return;

    const interval = setInterval(() => {
      if (!isTokenValid(token)) {
        logout();
      }
    }, 60_000);

    return () => clearInterval(interval);
  }, [token, logout]);

  const login = async (username: string, password: string) => {
    const response = await fetch(
      'https://dhanaprayoga.fiu.finfactor.in/finsense/API/V2/User/Login',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          header: {
            rid: crypto.randomUUID(),
            ts: new Date().toISOString(),
            channelId: 'finsense',
          },
          body: {
            userId: username,
            password: password,
          },
        }),
      }
    );

    if (!response.ok) {
      throw new Error('Login failed. Please check your credentials.');
    }

    const data = await response.json();
    const jwt = data?.body?.token || data?.token;

    if (!jwt || typeof jwt !== 'string') {
      throw new Error('Invalid response from server.');
    }

    if (!isTokenValid(jwt)) {
      throw new Error('Received an invalid token.');
    }

    localStorage.setItem(TOKEN_KEY, jwt);
    setToken(jwt);
  };

  return (
    <AuthContext.Provider value={{ token, isAuthenticated: !!token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
